/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { AtpStatus } from '@/lib/atp/atp-fields';

/**
 * Single source of truth for cart line-item unavailability checks across
 * product rows and checkout CTA disablement rules.
 */
export function isUnavailableCartItem(item: Record<string, unknown> | null | undefined): boolean {
    if (!item) return false;

    const inventory = item.inventory as Record<string, unknown> | null | undefined;
    const isTruthy = (value: unknown): boolean => value === true || value === 'true';
    const isFalsy = (value: unknown): boolean => value === false || value === 'false';

    return (
        isTruthy(item.showInventoryMessage) ||
        isTruthy(item.isProductUnavailable) ||
        isFalsy(item.orderable) ||
        isFalsy(inventory?.orderable)
    );
}

/**
 * Hard unavailability signals only (no message-only/soft flags).
 * Use this for catalog-level fallbacks where low-stock messaging should not
 * block checkout.
 */
export function isHardUnavailableCartItem(item: Record<string, unknown> | null | undefined): boolean {
    if (!item) return false;
    const inventory = item.inventory as Record<string, unknown> | null | undefined;
    const isFalsy = (value: unknown): boolean => value === false || value === 'false';
    const isTruthy = (value: unknown): boolean => value === true || value === 'true';
    return isTruthy(item.isProductUnavailable) || isFalsy(item.orderable) || isFalsy(inventory?.orderable);
}

/**
 * Checkout-blocking variant of unavailable checks.
 *
 * ATP should block checkout only when ATP enrichment has completed for the
 * basket (`c_atpStatus === "enriched"`). This avoids false positives when ATP
 * is unavailable or not yet resolved.
 */
export function isCheckoutBlockedCartItem(
    item: Record<string, unknown> | null | undefined,
    basketAtpStatus?: AtpStatus | null
): boolean {
    return getCheckoutBlockReason(item, basketAtpStatus) !== null;
}

export function getCheckoutBlockReason(
    item: Record<string, unknown> | null | undefined,
    basketAtpStatus?: AtpStatus | null
): 'hard_unavailable' | 'atp_unavailable' | null {
    if (!item) return null;
    // Checkout blocking is intentionally stricter than row-level inventory alerts:
    // low-stock copy (`showInventoryMessage`) should NOT block checkout.
    if (isHardUnavailableCartItem(item)) {
        return 'hard_unavailable';
    }
    const isAtpUnavailable = item.c_atpIsAvailable === false || item.c_atpIsAvailable === 'false';
    if (basketAtpStatus === 'enriched' && isAtpUnavailable) {
        return 'atp_unavailable';
    }
    return null;
}
